import Vector from "victor";
import { Tool } from "@/wbcontroller/types/Tool";
import ToolType from "@/wbcontroller/types/ToolType";
import WhiteboardControllerInterface from "../types/WhiteboardControllerInterface";
import { WBHUDState } from "@/store/WBHUDState";
import {
  SimplePointerEventHandler,
  SimplePointerEventHandlerDelegate,
} from "../SimplePointerEventHandler";
import { TextShape2 } from "./TextShape";
import ClickDetector from "../toolHelpers/ClickDetector";
import { ShapeType } from "../types/ShapeType";
import QuillDelta from "quill-delta";
import TextShapeDOMRepresentation from "./TextShapeDOMRepresentation";
import AddTextOperation from "./AddTextOperation";
import Rect from "../types/Rect";
import { shapeTruePosition2 } from "../util";
import { Scope } from "../types/Scope";
import { RangeStatic } from "quill";
import { AttributeKey } from "../types/attributes";

enum State {
  Creating,
  Resizing,
  Deselecting,
}

/**
 * What is this really?
 *
 * 1. Throttled live updates
 * 2. Special drag behavior
 *   a. If nothing selected, make new shape with limited width
 * 3. Special click behavior
 *   a. If something selected and click outside, deselect and commit
 *   b. If nothing selected and click anywhere, make new shape
 */
export default class TextTool
  implements SimplePointerEventHandlerDelegate, Tool
{
  toolType = ToolType.Text;

  // Populated by bind()
  wb?: WhiteboardControllerInterface;
  eventHandler: SimplePointerEventHandler;
  clickDetector = new ClickDetector();

  shapeID = "";
  nextZIndex = 0;
  isPointerDown = false;
  state = State.Creating;
  originalPoint = new Vector(0, 0);
  originalSize = new Vector(0, 0);
  originalSelection: RangeStatic | null = null;
  lastMaxWidth: number | null = null;

  shapeDR: TextShapeDOMRepresentation | null = null;

  constructor() {
    this.eventHandler = new SimplePointerEventHandler();
  }

  setShapeDR(shapeDR: TextShapeDOMRepresentation | null) {
    if (shapeDR === this.shapeDR) return;
    // console.log("Selected text shape:", shapeDR);
    this.shapeDR = shapeDR;
    if (this.wb?.drawing.focusedTextShapeIDBus.lastEmittedValue === shapeDR?.id)
      return;
    if (this.shapeDR) {
      this.wb?.drawing.focusTextShape(this.shapeDR.id, "text tool inner");
    } else {
      this.wb?.drawing.focusTextShape(null, "text tool deselect");
    }
  }

  // Settings passed down from
  // Vuex -> Whiteboard.vue -> WhiteboardController -> this method
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  settingsDidChange(settings: WBHUDState) {
    // TODO: apply stroke color to selection
    // this.shapePrototype.strokeColor = settings.strokeColor;
    // if (!this.shape) return;
    // this.shape.zIndex = nextZIndex;
  }

  nextZIndexDidChange(nextZIndex: number) {
    this.nextZIndex = nextZIndex;
  }

  private isPointerOnDragHandle(point: Vector): boolean {
    if (!this.wb || !this.shapeDR) return false;
    const worldPoint = this.wb.camera.convertScreenToWorld(point);
    // Sync size with wb.scss `.WBTextEditor_DragHandle`
    const dragRect = Rect.fromCenter(
      new Vector(
        this.shapeDR.boundingRect.maxX,
        this.shapeDR.boundingRect.centerY
      ),
      new Vector(18, 18) // 14px + 2px on each side
    );
    return dragRect.contains(worldPoint);
  }

  onPointerDown = (point: Vector) => {
    if (!this.wb) return;
    this.isPointerDown = true;
    const worldPoint = this.wb.camera.convertScreenToWorld(point);

    if (this.shapeDR && this.isPointerOnDragHandle(point)) {
      this.shapeDR.save();
      this.state = State.Resizing;
      this.originalPoint = point.clone();
      this.originalSize = this.shapeDR.boundingRect.size.clone();
      this.originalSelection = this.shapeDR.getSelection();
      return;
    }

    // Ignore further events inside the shape
    if (this.shapeDR && this.shapeDR.boundingRect.contains(worldPoint)) {
      return;
    }

    this.clickDetector.onPointerDown(point);

    if (this.shapeDR) {
      this.state = State.Deselecting;
    } else {
      this.state = State.Creating;
      this.shapeID = this.wb.makeID();
    }
  };

  onPointerMove = (point: Vector) => {
    if (this.wb && this.shapeDR && this.state === State.Resizing) {
      const worldPoint = this.wb.camera.convertScreenToWorld(point);
      const newMaxWidth = Math.max(
        50,
        worldPoint.x - shapeTruePosition2(this.shapeDR.shape).x
      );
      this.lastMaxWidth = newMaxWidth;

      this.wb?.shapeStore.sendUpsertSingle(Scope.Local, this.shapeDR.id, {
        key: AttributeKey.MaxWidth,
        value: newMaxWidth,
      });
    } else {
      this.clickDetector.onPointerMove(point);
    }
  };

  onPointerUp = () => {
    if (!this.isPointerDown) {
      this.abort();
      return;
    }
    this.isPointerDown = false;

    if (
      this.state === State.Resizing &&
      this.shapeDR &&
      this.lastMaxWidth !== null
    ) {
      this.wb?.shapeStore.sendUpsertSingle(Scope.Persist, this.shapeDR.id, {
        key: AttributeKey.MaxWidth,
        value: this.lastMaxWidth,
      });
      // During a drag, we lose the user's selection state. Restore it
      // once the drag is complete.
      if (this.originalSelection) {
        this.shapeDR.setSelection(this.originalSelection);
      }
    } else {
      if (!this.clickDetector.isClickAborted)
        this.onClick(this.clickDetector.lastScreenPoint);
    }
    this.clickDetector.onPointerUp();
    this.shapeID = "";
    this.state = State.Creating;
  };

  onClick = (point: Vector) => {
    if (!this.wb) return;

    const clickedShape = this.getShape(point);

    if (clickedShape) {
      this.setShapeDR(clickedShape);
    } else if (this.state === State.Deselecting) {
      this.setShapeDR(null);
    } else if (this.state === State.Creating) {
      const worldPoint = this.wb.camera.convertScreenToWorld(
        point.clone().subtract(new Vector(4, 4))
      );

      const textShape: TextShape2 = {
        shapeType: ShapeType.Text,
        zIndex: this.nextZIndex,
        text: new QuillDelta(),
        boundingBoxOrigin: [worldPoint.x, worldPoint.y],
        maxWidth: null,
        translate: [0, 0],
      };

      this.wb.applyOperation(
        new AddTextOperation(this.shapeID, textShape, new QuillDelta())
      );
      // This is pretty hacky :-/
      const touchedShapeID = this.wb.drawing.lastTouchedShapeID;
      if (!touchedShapeID) {
        console.error("Can't find the shape I just created");
        return;
      }
      const touchedShape = this.wb.drawing.indexedShapes[
        touchedShapeID
      ] as TextShapeDOMRepresentation;
      if (!touchedShape) {
        console.error("Can't find the shape I just created");
        return;
      }
      this.setShapeDR(touchedShape);
      this.wb.selectStickyAndBeginEditing(touchedShapeID);
      // this.wb.drawing.focusTextShape(
      //   touchedShapeID,
      //   "newly created text shape"
      // );
    }
  };

  abort() {
    this.state = State.Creating;
    this.clickDetector.abort();
  }

  getShape(screenPoint: Vector): TextShapeDOMRepresentation | null {
    if (!this.wb) return null;
    const worldPoint = this.wb.camera.convertScreenToWorld(screenPoint);
    for (let i = this.wb.drawing.orderedShapes.length - 1; i >= 0; i--) {
      const s = this.wb.drawing.orderedShapes[i];
      if (s.shape.shapeType !== ShapeType.Text) continue;
      if (s.hitTest(worldPoint, true)) return s as TextShapeDOMRepresentation;
    }
    return null;
  }

  onFocusChanged = (shapeID: string | null) => {
    if (shapeID === null) {
      this.shapeDR = null;
      this.state = State.Creating;
      return;
    }
    const shapeDR = this.wb?.drawing.indexedShapes[
      shapeID
    ] as TextShapeDOMRepresentation;
    if (!shapeDR) {
      this.shapeDR = null;
      this.state = State.Creating;
      return;
    }
    this.shapeDR = shapeDR;
    this.state = State.Deselecting;
  };

  bind(wb: WhiteboardControllerInterface) {
    this.wb = wb;
    this.eventHandler.bind(wb, this);
    this.wb.drawing.focusedTextShapeIDBus.stream.onValue(this.onFocusChanged);
    const lastID = this.wb.drawing.focusedTextShapeIDBus.lastEmittedValue;
    if (!lastID || !this.wb.drawing.indexedShapes[lastID]) return;
    const textShape = this.wb.drawing.indexedShapes[
      lastID
    ] as TextShapeDOMRepresentation;
    if (textShape) this.setShapeDR(textShape);
  }

  unbind() {
    this.eventHandler.unbind();
    this.wb?.drawing.focusedTextShapeIDBus.stream.offValue(this.onFocusChanged);
    this.wb = undefined;
  }
}
